import React from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { BylineConnectionsPlaceholderVisual } from '../BylineConnectionsPlaceholderVisual.tsx';

import { getReservedBylineIconCount } from './outboundBylineSsrState.ts';
import { useIncomingLinksSsrState } from './useIncomingLinksSsrState.ts';
import { useOutboundBylineSsrState } from './useOutboundBylineSsrState.ts';

type BylineConnectionsPlaceholderProps = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export const BylineConnectionsPlaceholder = ({
	contentId,
	connectionAncestors,
}: BylineConnectionsPlaceholderProps): React.JSX.Element | null => {
	// Read the preloaded outbound state on the client too (cache-only, hydrated from the SSR
	// preload). This lets the placeholder stay visible after the post-paint hydration and while the
	// LoadableAfterPaint chunk loads, instead of blanking — which caused the "SSR placeholder →
	// nothing → skeleton → item" flash. The result is derived purely from the hydrated cache and
	// dev-docs, so it is identical on the server and the client's first render (no hydration
	// mismatch). Pages with no connections still render nothing (no client footprint). CCINT-8752
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingLinksState = useIncomingLinksSsrState(contentId);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const reservedIconCount = getReservedBylineIconCount(
		outboundState,
		devDocsConnection !== null,
		incomingLinksState?.status === 'present',
	);
	if (reservedIconCount === 0) {
		return null;
	}

	return <BylineConnectionsPlaceholderVisual />;
};
